import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Bitcoin, Copy, CreditCard, Landmark, Loader2, Wallet, Check } from 'lucide-react';
import { copyText, haptic } from '@/lib/telegram';
import { useI18n } from '@/i18n';
import type { MethodKind, TxStatus } from '@/types/api';

/** The Bora4Bet wordmark: white "Bora", neon lime "4Bet". */
export function BrandMark({ size = 'md', glow = false }: { size?: 'sm' | 'md' | 'lg' | 'xl'; glow?: boolean }) {
    const cls = { sm: 'text-lg', md: 'text-2xl', lg: 'text-4xl', xl: 'text-5xl' }[size];
    return (
        <span className={clsx('font-display font-black italic tracking-tight leading-none select-none', cls)}>
            <span className="text-white">Bora</span>
            <span className={clsx('text-brand-400', glow && 'animate-lime-pulse')}>4Bet</span>
        </span>
    );
}

/** The PIX mark (four rounded squares in a diamond), teal like the artwork. */
function PixGlyph({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
            <rect x="8.2" y="1.5" width="7.6" height="7.6" rx="2" transform="rotate(45 12 5.3)" />
            <rect x="1.5" y="8.2" width="7.6" height="7.6" rx="2" transform="rotate(45 5.3 12)" />
            <rect x="14.9" y="8.2" width="7.6" height="7.6" rx="2" transform="rotate(45 18.7 12)" />
            <rect x="8.2" y="14.9" width="7.6" height="7.6" rx="2" transform="rotate(45 12 18.7)" />
        </svg>
    );
}

function TetherGlyph({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path fill="currentColor" d="M5 4h14v3.2h-5.4v2.2c3.6.2 6.2 1 6.2 2 0 1-2.6 1.8-6.2 2V20h-3.2v-6.6C6.8 13.2 4.2 12.4 4.2 11.4c0-1 2.6-1.8 6.2-2V7.2H5V4zm5.4 6.6c-2.4.1-4.1.5-4.1.8 0 .4 2.6.8 5.7.8s5.7-.4 5.7-.8c0-.3-1.7-.7-4.1-.8v1.3h-3.2v-1.3z" />
        </svg>
    );
}

const KIND_STYLE: Record<string, { icon: ComponentType<{ className?: string }>; tile: string }> = {
    pix: { icon: PixGlyph, tile: 'bg-accent-400/15 text-accent-300 border-accent-400/30' },
    crypto: { icon: Bitcoin, tile: 'bg-amber-400/10 text-amber-300 border-amber-300/25' },
    usdt: { icon: TetherGlyph, tile: 'bg-emerald-400/10 text-emerald-300 border-emerald-300/25' },
    link: { icon: Landmark, tile: 'bg-sky-400/10 text-sky-300 border-sky-300/25' },
    card: { icon: CreditCard, tile: 'bg-violet-400/10 text-violet-300 border-violet-300/25' },
    manual: { icon: Wallet, tile: 'bg-brand-400/10 text-brand-300 border-brand-400/25' },
    other: { icon: Wallet, tile: 'bg-white/5 text-ink-muted border-white/10' }
};

export function MethodIcon({ kind, size = 'md' }: { kind: MethodKind | 'other'; size?: 'sm' | 'md' | 'lg' }) {
    const s = KIND_STYLE[kind] || KIND_STYLE.other;
    const Icon = s.icon;
    const box = { sm: 'w-9 h-9 rounded-lg', md: 'w-11 h-11 rounded-xl', lg: 'w-14 h-14 rounded-2xl' }[size];
    const ic = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' }[size];
    return <div className={clsx('shrink-0 flex items-center justify-center border', box, s.tile)}><Icon className={ic} /></div>;
}

export function Spinner({ className }: { className?: string }) {
    return <Loader2 className={clsx('animate-spin', className || 'w-5 h-5')} />;
}

export function PageLoading() {
    return (
        <div className="space-y-3 animate-fade-in">
            <div className="skeleton h-24" />
            <div className="skeleton h-16" />
            <div className="skeleton h-16" />
        </div>
    );
}

/** Full-screen state (banned, maintenance, errors, "open in Telegram"). */
export function Screen({ icon, title, text, children }: { icon?: ReactNode; title: string; text?: string; children?: ReactNode }) {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 gap-5 animate-fade-in-up">
            <BrandMark size="lg" />
            {icon && <div className="text-brand-300">{icon}</div>}
            <div className="space-y-2 max-w-xs">
                <h1 className="text-xl font-bold">{title}</h1>
                {text && <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-line">{text}</p>}
            </div>
            {children && <div className="w-full max-w-xs flex flex-col gap-2">{children}</div>}
        </div>
    );
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
    return (
        <div className="mb-4">
            <h1 className="text-xl font-extrabold font-display">{title}</h1>
            {subtitle && <p className="text-sm text-ink-muted mt-0.5">{subtitle}</p>}
        </div>
    );
}

export function CopyRow({ label, value, display, action, mono = true }: { label: string; value: string; display?: string; action?: ReactNode; mono?: boolean }) {
    const { t } = useI18n();
    const [done, setDone] = useState(false);
    useEffect(() => { if (!done) return; const id = setTimeout(() => setDone(false), 1500); return () => clearTimeout(id); }, [done]);
    return (
        <div className="rounded-xl bg-white/[.03] border border-white/10 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2 text-[11px] text-ink-muted mb-1"><span>{label}</span>{action}</div>
            <div className="flex items-center gap-2">
                <span className={clsx('flex-1 min-w-0 break-all text-sm', mono && 'font-mono')}>{display ?? value}</span>
                <button
                    type="button"
                    className="shrink-0 p-2 rounded-lg bg-brand-400/10 text-brand-300 border border-brand-400/25 active:scale-95 transition"
                    onClick={async () => {
                        if (await copyText(value)) { haptic('success'); setDone(true); toast.success(t('common.copied')); }
                    }}
                    aria-label={t('common.copy')}
                >
                    {done ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}

export function InfoRow({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
            <span className="text-ink-muted">{label}</span>
            <span className={clsx('text-right', strong && 'font-bold text-white')}>{value}</span>
        </div>
    );
}

export function StatusPill({ status }: { status: TxStatus }) {
    const { t } = useI18n();
    const cls = status === 'confirmed' ? 'text-brand-200 bg-brand-400/15 border-brand-400/30'
        : status === 'rejected' ? 'text-red-200 bg-red-500/10 border-red-400/30'
        : 'text-amber-200 bg-amber-400/10 border-amber-300/30';
    return <span className={clsx('badge border', cls)}>{t(`status.${status}`)}</span>;
}

/** Amount input with the method's limits and quick chips inside them. */
export function AmountField({ value, onChange, currency, min, max, error, presets }: {
    value: string; onChange: (v: string) => void; currency: string; min: number; max: number; error?: string | null; presets?: number[];
}) {
    const { t } = useI18n();
    const chips = (presets || []).filter(p => p >= min && (!max || p <= max));
    return (
        <div>
            <label className="label">{t('common.amount')} ({currency})</label>
            <input
                className={clsx('input text-lg font-semibold', error && 'border-red-400/60 focus:border-red-400 focus:ring-red-400/20')}
                inputMode="decimal" autoComplete="off" placeholder={t('common.amountPlaceholder')}
                value={value} onChange={e => onChange(e.target.value.replace(/[^\d.,]/g, ''))}
            />
            {chips.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {chips.map(p => (
                        <button key={p} type="button" onClick={() => { haptic('selection'); onChange(String(p)); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 border border-white/10 active:scale-95 hover:border-brand-400/40">
                            {p}
                        </button>
                    ))}
                </div>
            )}
            {error ? <p className="text-xs text-red-300 mt-1.5 px-0.5">{error}</p> : null}
        </div>
    );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'ok'; children: ReactNode }) {
    const cls = tone === 'warn' ? 'bg-amber-400/10 border-amber-300/25 text-amber-100'
        : tone === 'ok' ? 'bg-brand-400/10 border-brand-400/25 text-brand-100'
        : 'bg-white/[.03] border-white/10 text-ink-muted';
    return <div className={clsx('rounded-xl border px-3 py-2.5 text-xs leading-relaxed whitespace-pre-line', cls)}>{children}</div>;
}
